import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Loader2, CheckCircle, XCircle, Shield, MapPin, Eye, EyeOff } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'

interface InvitationData {
  id: string
  email: string
  role_name: string
  franchise_regions: string[]
  expires_at: string
  accepted_at?: string
  message?: string
}

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const { success: toastSuccess, error: toastError } = useToast()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [invitation, setInvitation] = useState<InvitationData | null>(null)
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null)

  // Validate invitation token
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValidToken(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('user_invitations')
          .select(`
            id,
            email,
            franchise_regions,
            expires_at,
            accepted_at,
            message,
            roles!inner(name)
          `)
          .eq('token', token)
          .single()

        if (error || !data) {
          setIsValidToken(false)
          return
        }

        // Check if invitation is expired
        if (new Date(data.expires_at) < new Date()) {
          setIsValidToken(false)
          toastError('Invitation expired', 'This invitation has expired')
          return
        }

        // Check if already accepted
        if (data.accepted_at) {
          setIsValidToken(false)
          toastError('Invitation already accepted', 'This invitation has already been accepted')
          return
        }

        const roleName = Array.isArray((data as any).roles)
          ? (data as any).roles[0]?.name
          : (data as any).roles?.name

        setInvitation({
          ...data,
          role_name: roleName || 'user'
        })
        setIsValidToken(true)
      } catch (error) {
        console.error('Error validating token:', error)
        setIsValidToken(false)
      }
    }

    validateToken()
  }, [token])

  // Accept invitation mutation
  const acceptInvitationMutation = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('No token provided')

      const { data, error } = await supabase.functions.invoke('accept-invitation', {
        body: {
          token,
          password,
          full_name: fullName.trim() || undefined
        }
      })

      if (error) throw error
      return data
    },
    onSuccess: () => {
      toastSuccess('Account created', 'You can now sign in.')
      navigate('/login', {
        state: {
          message: 'Account created successfully. Please sign in with your credentials.',
          email: invitation?.email
        }
      })
    },
    onError: (error: any) => {
      toastError('Failed to accept invitation', error.message || 'Please try again.')
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (!password || !confirmPassword) {
      toastError('Missing fields', 'Please fill in all required fields')
      return
    }

    if (password !== confirmPassword) {
      toastError('Passwords do not match', 'Please re-enter your password')
      return
    }

    const passwordRegex = /^(?=.*[0-9!@#$%^&*])/
    if (password.length < 8 || !passwordRegex.test(password)) {
      toastError('Weak password', 'Password must be at least 8 characters and include a number or symbol')
      return
    }

    acceptInvitationMutation.mutate()
  }

  // Loading state
  if (isValidToken === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p>Validating invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Invalid token
  if (!isValidToken || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
              <p className="text-gray-600 mb-6">
                This invitation link is invalid, expired, or has already been used.
              </p>
              <Button onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center flex items-center justify-center gap-2">
            <CheckCircle className="h-6 w-6 text-green-500" />
            Accept Invitation
          </CardTitle>
          <CardDescription className="text-center">
            Complete your account setup for FixMyBrick Admin Dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Invitation Details */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-3">
            <div>
              <div className="text-sm font-medium text-gray-700">Email</div>
              <div className="text-sm text-gray-900">{invitation.email}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700">Role</div>
              <Badge variant={invitation.role_name === 'admin' ? 'destructive' : 'default'} className="mt-1">
                <Shield className="h-3 w-3 mr-1" />
                {invitation.role_name}
              </Badge>
            </div>
            {invitation.franchise_regions.length > 0 && (
              <div>
                <div className="text-sm font-medium text-gray-700">Franchise Access</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {invitation.franchise_regions.map((region) => (
                    <Badge key={region} variant="outline" className="text-xs">
                      <MapPin className="h-3 w-3 mr-1" />
                      {region}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {invitation.message && (
              <div>
                <div className="text-sm font-medium text-gray-700">Message</div>
                <div className="text-sm text-gray-900">{invitation.message}</div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name (Optional)</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Your full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={acceptInvitationMutation.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={acceptInvitationMutation.isPending}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-500">Min 8 chars, 1 number or symbol</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={acceptInvitationMutation.isPending}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {password && confirmPassword && password !== confirmPassword && (
              <p className="text-sm text-red-600">Passwords do not match</p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={
                acceptInvitationMutation.isPending ||
                !password ||
                !confirmPassword ||
                password !== confirmPassword ||
                password.length < 8
              }
            >
              {acceptInvitationMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Accept Invitation & Create Account'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/login')}>
                Sign in here
              </Button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
